import { Accessor, Setter, createSignal } from 'solid-js'
import { RemoteInputContext } from './RemoteInputContext'
import { useDispatch, useSelector } from '../../store'
import { pushNotification } from '@te_react/volt-store/lib/actions'

export interface RemoteInputProviderProps {
    children: any
}

export const RemoteInputProvider = (props: RemoteInputProviderProps) => {
    const dispatch = useDispatch()

    const [remoteNumInput, setRemoteNumInput] = createSignal('')
    const [layoutInterval, setLayoutInterval] = createSignal<NodeJS.Timeout>()
    const [remoteInputPlay, setRemoteInputPlay] = createSignal<any>(false)
    const [restrictTunning, setRestrictTunning] = createSignal<boolean>(false)

    const channelsState = useSelector((state: any) => state?.channels)
    const programsState = useSelector((state: any) => state?.programs)
    const playerData = useSelector((state: any) => state.player.streamUrl)
    const channelId = useSelector((state: any) => state.player.channelId)

    const customPlayback = () => {
        if (remoteNumInput()) {
            const kidsProfile = localStorage.getItem('isKidsProfile')
            let allChannelsIds: any
            let allChannelsData: any
            if (kidsProfile == "true") {
                allChannelsIds = Object.keys(channelsState()?.data)?.filter(
                    e => channelsState()?.data[e]?.isKidsChannel
                )
                allChannelsData = allChannelsIds.map(e => channelsState()?.data[e])
            } else {
                allChannelsData = Object.values(channelsState()?.data)
            }

            let prevNearbyAsset: any = undefined
            let nextNearbyAsset: any = undefined
            let result: any = allChannelsData.find((x: any) => {
                if (x?.channelNumber === Number(remoteNumInput())) return true
                else {
                    if (
                        (x?.channelNumber < Number(remoteNumInput()) &&
                            x?.channelNumber > prevNearbyAsset?.channelNumber) ||
                        (!prevNearbyAsset && x?.channelNumber < Number(remoteNumInput()))
                    ) {
                        prevNearbyAsset = x
                    } else if (
                        (x?.channelNumber > Number(remoteNumInput()) &&
                            x?.channelNumber < nextNearbyAsset?.channelNumber) ||
                        !nextNearbyAsset
                    ) {
                        nextNearbyAsset = x
                    }
                    return false
                }
            })
            if (!result && (prevNearbyAsset || nextNearbyAsset)) {
                if (prevNearbyAsset && nextNearbyAsset) {
                    const moveToPrev =
                        Math.abs(Number(remoteNumInput()) - prevNearbyAsset?.channelNumber) <=
                        Math.abs(nextNearbyAsset?.channelNumber - Number(remoteNumInput()))
                    moveToPrev ? (result = prevNearbyAsset) : (result = nextNearbyAsset)
                } else if (prevNearbyAsset) {
                    result = prevNearbyAsset
                } else if (nextNearbyAsset) {
                    result = nextNearbyAsset
                }
            }

            const liveProgram = Object.keys(programsState())
                .map(key => programsState()?.[key])
                .find(p => p?.channelId == result?.id && p?.isLiveNow?.())
            if (result?.id && liveProgram) {
                if (playerData()) {
                    if (result?.id !== channelId()) {
                        setRemoteInputPlay(liveProgram ?? result)
                    } else {
                        remoteNumInputCleanUp()
                    }
                } else {
                    setRemoteInputPlay(liveProgram ?? result)
                    remoteNumInputCleanUp()
                }
            } else {
                dispatch(
                    pushNotification({
                        message: {
                            tag: 'programsLoading',
                            type: "information"
                        },
                    }),
                )
                remoteNumInputCleanUp()
            }
        }
    }

    const remoteNumInputCleanUp = () => {
        setRemoteNumInput('')
        layoutInterval() && clearInterval(layoutInterval())
        setRemoteInputPlay(undefined)
    }

    const setRemoteInputConditionally = (value: string) => {
        if (restrictTunning()) {
            return
        } else {
            setRemoteNumInput(value)
        }
    }

    return (
        <RemoteInputContext.Provider
            value={{
                remoteNumInput: remoteNumInput,
                remoteNumInputCleanUp: remoteNumInputCleanUp,
                customPlayback: customPlayback,
                setRemoteNumInput: setRemoteInputConditionally,
                setLayoutInterval,
                layoutInterval,
                remoteInputPlay: remoteInputPlay,
                setRestrictTunning,
            }}
        >
            {props.children}
        </RemoteInputContext.Provider>
    )
}

export interface RemoteInputProviderBehavior {
    remoteNumInput: Accessor<any>
    remoteNumInputCleanUp: () => void
    customPlayback: () => void
    setRemoteNumInput: (value: string) => void
    setLayoutInterval: Setter<NodeJS.Timeout | undefined>
    layoutInterval: Accessor<any>
    remoteInputPlay: Accessor<any>
    setRestrictTunning: Setter<boolean>
}
